// RowRename — the inline rename affordance of the lifecycle pattern
// (master audit R5: "Byt namn" edits in place, no navigation).
//
// A text input that temporarily REPLACES a row's label. Enter commits,
// Escape cancels, leaving the field commits (the Finder/VS Code
// convention — an author who typed a new name and clicked away meant
// it). A blank or unchanged name cancels instead of committing, so the
// pattern can never submit a no-op or an empty title.
//
// The draft is the input's own view state (like Tree's roving focus) —
// the OUTCOME is what reports up, via two distinct intents: onSubmit
// with the new title, or onCancel. The `select()` on mount is the one
// sanctioned DOM nudge (focus-management, same class as Tree's
// `.focus()`).

import { useCallback, useRef, useState } from "react";
import "./RowRename.css";

export type RowRenameProps = {
    // The current title the field starts from.
    readonly initial: string;
    // Accessible name for the input (caller-owned / localised).
    readonly label: string;
    readonly onSubmit: (title: string) => void;
    readonly onCancel: () => void;
    readonly className?: string;
    // The initial text is a SUGGESTION (naming something new), so keeping
    // it submits it; by default an unchanged name cancels.
    readonly suggested?: boolean;
};

export const RowRename = ({
    initial,
    label,
    onSubmit,
    onCancel,
    className,
    suggested = false,
}: RowRenameProps) => {
    const [draft, setDraft] = useState(initial);
    // Select the text once, on mount. The ref callback must be stable: an
    // inline arrow is a new function every render, so React re-ran it after
    // each keystroke and every typed character replaced the whole field.
    const selectOnMount = useCallback(
        (el: HTMLInputElement | null) => el?.select(),
        [],
    );
    // Guards the blur that follows Enter/Escape from double-reporting.
    const settled = useRef(false);

    const commit = () => {
        if (settled.current) return;
        settled.current = true;
        const title = draft.trim();
        if (title === "" || (title === initial && !suggested)) onCancel();
        else onSubmit(title);
    };
    const cancel = () => {
        if (settled.current) return;
        settled.current = true;
        onCancel();
    };

    return (
        <input
            className={`bilda-row-rename${className ? ` ${className}` : ""}`}
            type="text"
            aria-label={label}
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            onKeyDown={(e) => {
                if (e.key === "Enter") commit();
                else if (e.key === "Escape") cancel();
            }}
            onBlur={commit}
            ref={selectOnMount}
        />
    );
};
